import { toast } from 'react-toastify';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import { setPageTitle } from 'store/actions/header';
import { removeOneEventFromStore, loadEventsToStore } from 'store/actions/event';
import styles from '../event.module.css';
import cx from 'classnames';
import { PermissionsContext } from 'contexts/permissions';
import { useEventsService } from 'hooks/automation/event';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { Badge } from 'app/shared/badge';
import { SegmentedTabs } from 'app/shared/segmented-tabs';
import { Spacer } from 'app/layouts/generic';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import { EventForm } from '../form';

// Custom events are stored with type 'user'.
const event_type_tabs = [
  { value: 'user', label: 'Custom' },
  { value: 'system', label: 'System' }
];

export const AutomationEvents = () => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const event_in_store = useSelector((state) => state.events);
  const { permissions } = useContext(PermissionsContext);

  const { fetchEvents, removeEvent, searchEvents } = useEventsService();
  const { fetchSubscription } = useSubscriptionService();

  const [events, setEvents] = useState([]);
  const [has_insights_subscription, setHasInsightsSubscription] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total_events, setTotalEvents] = useState(0);
  const [event_type, setEventType] = useState('user');
  const [event_to_edit, setEventToEdit] = useState({});
  const [show_form_modal, setShowFormModal] = useState(false);
  // Bumped on every open so the form starts fresh instead of keeping old values.
  const [form_key, setFormKey] = useState(0);
  const [show_event_to_delete_modal, setShowEventToDeleteModal] = useState(false);
  const [event_to_delete, setEventToDelete] = useState({});

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Event', path: '.' }]));
    fetchSubscription({ query_string: `plan_code=app_insights&status=active` }).then(
      ({ subscriptions: [subscription] }) => {
        setHasInsightsSubscription(subscription && subscription.id);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEvents(Object.values(event_in_store));
  }, [event_in_store]);

  const config = {
    actions: {
      single: () => [
        [
          {
            label: 'Edit',
            value: 'edit',
            hidden: !permissions['automation_event:update']
          }
        ],
        [
          {
            color: 'red',
            label: 'Delete',
            value: 'delete',
            hidden: !permissions['automation_event:delete']
          }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Name',
        key: 'name',
        searchable: true,
        isTitle: true
      },
      {
        title: 'Code',
        searchable: true,
        key: 'code',
        isTagline: true
      },
      {
        title: 'Type',
        key: 'type',
        formatter: (value) => {
          switch (value) {
            case 'system':
              return <Badge type="blue" text="System" />;
            default:
              return <Badge type="orange" text="Custom" />;
          }
        },
        isBadge: true
      }
    ],
    is_search_mode,
    items: events.sort((a, b) => b.time_stamp - a.time_stamp),
    num_of_rows: 10,
    search_key: 'code,name',
    search_text: ''
  };

  const parseEvents = (data) => {
    const parsed_events = data?.map((event) => {
      const [code] = event.code.split(':');
      const new_element = {
        ...event,
        code
      };
      return new_element;
    });
    return parsed_events;
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;
    if (type === 'single') {
      if (name === 'edit') {
        if (data.type === 'system') {
          return toast.error('You cannot edit a system event');
        }

        openForm(data);
        return;
      }
      if (name === 'delete') {
        setEventToDelete(data);
        setShowEventToDeleteModal(true);
      }
    }
  };

  const handleDataRequest = async (page = 0, population = 10) => {
    try {
      setLoading(true);
      const { events, size } = await fetchEvents({
        query_string: `type=${event_type}&page=${page}&population=${population}&sort_by=-created_on`
      });
      const parsed_events = parseEvents(events);
      dispatch(loadEventsToStore(parsed_events));
      setTotalEvents(size);
    } catch (e) {
      dispatch(loadEventsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page = 0, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { events, size } = await searchEvents(keys, keyword, {
        query_string: `type=${event_type}&sort_by=-created_on&page=${page}&population=${population}`
      });

      const parsed_events = parseEvents(events);
      dispatch(loadEventsToStore(parsed_events));
      setTotalEvents(size);
    } catch (e) {
      dispatch(loadEventsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSingleDeletion = async (permitted) => {
    if (permitted) {
      if (event_to_delete.in_use) {
        return toast.error('You cannot delete an event in use');
      }

      if (event_to_delete.type === 'system') {
        return toast.error('You cannot delete a system event');
      }

      const result = await removeEvent(event_to_delete.id);
      if (result) {
        toast.success('Event deleted successfully');
        dispatch(removeOneEventFromStore(event_to_delete.id));
      }
    }
    setShowEventToDeleteModal(false);
  };

  const openForm = (event = {}) => {
    setEventToEdit(event);
    setFormKey((key) => key + 1);
    setShowFormModal(true);
  };

  const handleFormSave = (event) => {
    setShowFormModal(false);
    if (event_to_edit.id) return;
    // New triggers are custom: show them on the Custom tab, with a fresh count.
    if (event_type !== 'user') return handleEventTypeChange('user');
    setTotalEvents((count) => count + 1);
  };

  const handleEventTypeChange = (type) => {
    dispatch(loadEventsToStore([]));
    setEventType(type);
  };

  const event_type_toggle = (
    <SegmentedTabs
      options={event_type_tabs}
      value={event_type}
      onChange={handleEventTypeChange}
      label="Trigger type"
    />
  );

  const table_actions = (
    <Button
      icon_name="add"
      text="Create trigger"
      onClick={() => openForm()}
      disabled={!permissions['automation_event:create']}
    />
  );

  const handleItemClick = (data) => {
    if (!has_insights_subscription) return;
    navigateTo(`/insights/app/${data.code}`);
  };

  return (
    <div className={cx(styles.wrapper, 'dt-content-wrapper')}>
      {is_mobile_view ? (
        <>
          <div className={styles.mobileToolbar}>
            {event_type_toggle}
            {table_actions}
          </div>
          <Spacer multiple={2} />
          <MobileDatatable
            key={event_type}
            config={config}
            action={handleDatatableAction}
            onClick={handleItemClick}
            onListModeChange={setSearchMode}
            onDataRequest={handleDataRequest}
            onSearchRequest={handleSearchRequest}
          />
        </>
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_events
          }}
          action={handleDatatableAction}
          onClick={handleItemClick}
          checkbox
          loading_data={loading}
          header_tabs={event_type_toggle}
          reset_key={event_type}
          table_actions={table_actions}
          request_complete={!loading}
          onDataRequest={handleDataRequest}
          loadingSearch={() => {}}
          onSearchRequest={handleSearchRequest}
        />
      )}
      <GmModal
        position="right"
        show_modal={show_form_modal}
        show_title
        title={event_to_edit.id ? 'Update custom trigger' : 'Create custom trigger'}
        onClose={() => setShowFormModal(false)}
      >
        <EventForm
          key={form_key}
          data={event_to_edit}
          onSave={handleFormSave}
          onCancel={() => setShowFormModal(false)}
        />
      </GmModal>
      <ConfirmationDialog
        action_text="Yes, delete"
        icon_name="trash"
        type="error"
        title="Delete Event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        callback={handleSingleDeletion}
        is_open={show_event_to_delete_modal}
      />
    </div>
  );
};
